import * as React from "react"
import { createPortal } from "react-dom"
import { cn } from "../../lib/utils"
import { X } from "lucide-react"

const DialogContext = React.createContext({})
const openDialogs = []
let previousBodyOverflow = ""

const Dialog = ({ children, open, onOpenChange }) => {
  const [isOpen, setIsOpen] = React.useState(open || false)
  const titleId = React.useId()
  
  React.useEffect(() => {
    if (open !== undefined) {
      setIsOpen(open)
    }
  }, [open])
  
  const handleOpenChange = (newOpen) => {
    if (open === undefined) {
      setIsOpen(newOpen)
    }
    onOpenChange?.(newOpen)
  }
  
  return (
    <DialogContext.Provider value={{ isOpen, onOpenChange: handleOpenChange, titleId }}>
      {children}
    </DialogContext.Provider>
  )
}

const DialogTrigger = React.forwardRef(({ className, children, ...props }, ref) => {
  const { onOpenChange } = React.useContext(DialogContext)
  
  return (
    <button
      ref={ref}
      type="button"
      onClick={() => onOpenChange(true)}
      className={className}
      {...props}
    >
      {children}
    </button>
  )
})
DialogTrigger.displayName = "DialogTrigger"

const DialogPortal = ({ children, portal = false }) => {
  const { isOpen } = React.useContext(DialogContext)
  
  if (!isOpen) return null
  
  return portal ? createPortal(children, document.body) : <>{children}</>
}

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => {
  const { onOpenChange } = React.useContext(DialogContext)
  
  return (
    <div
      ref={ref}
      className={cn(
        "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      )}
      onClick={() => onOpenChange(false)}
      {...props}
    />
  )
})
DialogOverlay.displayName = "DialogOverlay"

/**
 * Responsive width ladder for dialogs.
 *
 * A dialog must NEVER scroll horizontally — only vertically. Two rules make
 * that hold:
 *   1. On phones every dialog is the full viewport width minus a small gutter,
 *      so there is no artificial narrow column for content to spill out of.
 *   2. On larger screens the dialog GROWS with the breakpoint instead of being
 *      frozen at a phone-ish width, so multi-column forms have room to breathe
 *      rather than overflowing (and getting clipped by `overflow-x-hidden`).
 *
 * Pick the size from the CONTENT, not from the old class:
 *   xs   — one short line / a single field
 *   sm   — confirmations, delete prompts
 *   md   — small single-column forms
 *   lg   — standard forms (a handful of fields, some side-by-side)
 *   xl   — rich forms, detail panels
 *   2xl  — wide detail views, pickers with lists
 *   3xl  — dense tables, roster/grid pickers, builders
 *   full — takes essentially the whole screen
 *
 * Call sites must use this prop and must NOT pass their own `max-w-*`: an
 * unprefixed `max-w-md` at a call site collides with the prefixed
 * `sm:max-w-*` here (equal specificity, different Tailwind keys, so
 * tailwind-merge keeps both) and silently re-narrows the dialog on desktop.
 */
const DIALOG_SIZES = {
  xs: "sm:max-w-sm",
  sm: "sm:max-w-md",
  md: "sm:max-w-lg",
  lg: "sm:max-w-xl lg:max-w-2xl",
  xl: "sm:max-w-2xl lg:max-w-3xl",
  "2xl": "sm:max-w-3xl lg:max-w-4xl xl:max-w-5xl",
  "3xl": "sm:max-w-4xl lg:max-w-5xl xl:max-w-6xl",
  full: "sm:max-w-[calc(100vw-4rem)]",
}

const DialogContent = React.forwardRef(({ className, children, size = "md", portal = false, showCloseButton = true, ...props }, ref) => {
  const { isOpen, onOpenChange, titleId } = React.useContext(DialogContext)
  const contentRef = React.useRef(null)
  const closeRef = React.useRef(onOpenChange)
  closeRef.current = onOpenChange
  const setRef = React.useCallback(node => {
    contentRef.current = node
    if (typeof ref === "function") ref(node)
    else if (ref) ref.current = node
  }, [ref])

  React.useEffect(() => {
    if (!isOpen || !contentRef.current) return undefined
    const node = contentRef.current
    const active = document.activeElement
    if (!openDialogs.length) {
      previousBodyOverflow = document.body.style.overflow
      document.body.style.overflow = "hidden"
    }
    openDialogs.push(node)
    // Focus the surface, not an input: don't open the phone keyboard on arrival.
    node.focus({ preventScroll: true })
    const keydown = event => {
      if (openDialogs[openDialogs.length - 1] !== node) return
      if (event.key === "Escape") {
        event.preventDefault()
        event.stopPropagation()
        closeRef.current(false)
      }
      if (event.key !== "Tab") return
      const items = [...node.querySelectorAll('button, a[href], input, select, textarea, [tabindex]')]
        .filter(el => !el.disabled && el.tabIndex >= 0 && !el.hidden && el.getAttribute("type") !== "hidden" && el.getClientRects().length > 0)
      const first = items[0]
      const last = items[items.length - 1]
      if (!first) {
        event.preventDefault()
        node.focus()
      } else if (event.shiftKey && (document.activeElement === first || document.activeElement === node)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === node)) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener("keydown", keydown)
    return () => {
      document.removeEventListener("keydown", keydown)
      const index = openDialogs.indexOf(node)
      if (index !== -1) openDialogs.splice(index, 1)
      if (!openDialogs.length) document.body.style.overflow = previousBodyOverflow
      if (active?.isConnected) active.focus({ preventScroll: true })
    }
  }, [isOpen])

  return (
    <DialogPortal portal={portal}>
      <DialogOverlay />
      <div
        ref={setRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={props["aria-label"] ? undefined : titleId}
        tabIndex={-1}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-4 sm:p-6 shadow-lg duration-200 rounded-lg",
          // Width: near-full-bleed on phones, growing with the breakpoint.
          "w-[calc(100vw-1rem)] sm:w-[calc(100vw-4rem)]",
          // Height: scroll vertically only, never horizontally.
          "dialog-safe-viewport max-h-[calc(100dvh-1rem)] sm:max-h-[calc(100dvh-4rem)] overflow-y-auto overflow-x-hidden overscroll-contain",
          // Long words / emails / URLs wrap instead of forcing the dialog wide,
          // and grid children may shrink below their intrinsic min-content width
          // (a grid item defaults to `min-width:auto`, the usual overflow cause).
          "break-words [&>*]:min-w-0",
          DIALOG_SIZES[size] || DIALOG_SIZES.md,
          className
        )}
        onClick={(e) => e.stopPropagation()}
        {...props}
      >
        {children}
        {showCloseButton && <button
          type="button"
          className="absolute left-1 top-1 sm:left-3 sm:top-3 flex h-11 w-11 items-center justify-center rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
          onClick={() => onOpenChange(false)}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">إغلاق</span>
        </button>}
      </div>
    </DialogPortal>
  )
})
DialogContent.displayName = "DialogContent"

// `pl-7` keeps a long title from running underneath the absolutely-positioned
// close button (which sits at `left-4 top-4`, i.e. the leading edge in RTL).
const DialogHeader = ({ className, ...props }) => (
  <div
    className={cn("flex flex-col space-y-1.5 pl-9 text-center sm:text-right", className)}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({ className, ...props }) => (
  <div
    className={cn(
      // `sm:flex-wrap` so a footer with several actions wraps to a second row
      // instead of pushing the dialog wider than the screen.
      "flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-start",
      "sticky bottom-0 z-10 mt-2 -mx-4 -mb-4 border-t bg-background px-4 pt-3 pb-4 sm:-mx-6 sm:-mb-6 sm:px-6 sm:pb-6",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, children, ...props }, ref) => {
  const { titleId } = React.useContext(DialogContext)
  return <h2
    ref={ref}
    id={titleId}
    className={cn("text-lg font-semibold leading-snug tracking-tight", className)}
    {...props}
  >{children}</h2>
})
DialogTitle.displayName = "DialogTitle"

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = "DialogDescription"

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
